/**
 * `<GlobalStatusBar>` — the always-mounted status strip (frontend.md §2.1, §2.4).
 *
 * Feeds from `useSystemStatusQuery` (notes count + dependency reachability). Shows:
 *
 *   ● Healthy · 248 notes · 1,284 chunks · 462 tags · 146 entities   retrieval ok · edges ok · db ok
 *
 * Per GAP_ANALYSIS.md §X1 (P5): chunks / tags / entities pills. (The labeled
 * session cost was removed 2026-05-21 with the rest of the cost telemetry.)
 *
 * v4 P7 (2026-05-18, GAP_ANALYSIS_R2.md §S3a/§X1): chunks/tags/entities are
 * now wired through `SystemStatus.totalChunks` / `totalTags` / `totalEntities`.
 * The fields are optional on the TS type so an older backend still parses;
 * em-dash is shown for any field that comes back undefined (skeleton-before-
 * data rule preserved).
 *
 * The status bar also exposes three named SLOTS (`left` / `center` / `right`)
 * for guest content registered through the `ShellMount`.
 */

import { useSystemStatusQuery, useConnectivity } from '@/hooks';
import { APP_VERSION, BUILD_TIME, formatBuildTime } from '@/version';
import { useShellMountSnapshot } from './ShellMountContext';

export function GlobalStatusBar(): JSX.Element {
  const mount = useShellMountSnapshot();
  const statusQuery = useSystemStatusQuery();
  const status = statusQuery.data;
  // Backend reachability (see providers/connectivity.ts). When the web services can't be
  // reached, the app keeps running from its local cache and we say so HERE — the one place
  // connection trouble surfaces — rather than letting it erupt across the UI.
  const reachable = useConnectivity();

  const leftSlot = mount.getStatusBarSlot('left');
  const centerSlot = mount.getStatusBarSlot('center');
  const rightSlot = mount.getStatusBarSlot('right');

  // Connectivity wins the health pill: an unreachable backend is the user-facing headline
  // ("Web Services Unreachable", amber), shown ahead of any stale healthy/degraded read.
  const healthLabel = !reachable
    ? 'Web Services Unreachable'
    : statusQuery.isError
      ? 'Backend unreachable'
      : status?.healthy
        ? 'Healthy'
        : 'Degraded';
  const healthKind = !reachable
    ? 'warn'
    : statusQuery.isError || !status?.healthy
      ? 'bad'
      : 'ok';
  const healthTitle = !reachable
    ? 'No connection to Aletheia’s web services — showing your locally cached workspace. Reconnecting automatically…'
    : statusQuery.isError
      ? 'Backend unreachable'
      : undefined;

  return (
    <footer className="status-bar" aria-label="System status">
      <div className="status-bar__slot status-bar__slot--left">
        {/* Health pill — pulsing dot + colored label. .ok = good (heartbeat
            animation), .bad = bad (steady). */}
        <span
          className={`status-bar__pill status-bar__pill--${healthKind}`}
          title={healthTitle}
        >
          <span className="status-bar__pill-dot" aria-hidden="true" />
          {healthLabel}
        </span>

        {/* v4 P5 (§X1) + P7 wiring: corpus stat pills — notes / chunks / tags
            / entities. Each pill is cool-blue number + fg-3 unit. All four
            now pull from useSystemStatusQuery; a field that's still undefined
            (older backend) falls back to em-dash so the slot stays visible. */}
        {status !== undefined && (
          <>
            <span className="status-bar__sep" aria-hidden="true">·</span>
            <span className="status-bar__metric" title="Live notes in workspace">
              {status.totalLiveNotes.toLocaleString()}
              <span className="status-bar__metric-unit">notes</span>
            </span>
            <span className="status-bar__sep" aria-hidden="true">·</span>
            <span className="status-bar__metric" title="Indexed chunks across the corpus">
              {status.totalChunks !== undefined ? (
                status.totalChunks.toLocaleString()
              ) : (
                <span style={{ color: 'var(--fg-4)' }}>—</span>
              )}
              <span className="status-bar__metric-unit">chunks</span>
            </span>
            <span className="status-bar__sep" aria-hidden="true">·</span>
            <span className="status-bar__metric" title="Distinct tags across the corpus">
              {status.totalTags !== undefined ? (
                status.totalTags.toLocaleString()
              ) : (
                <span style={{ color: 'var(--fg-4)' }}>—</span>
              )}
              <span className="status-bar__metric-unit">tags</span>
            </span>
            <span className="status-bar__sep" aria-hidden="true">·</span>
            <span className="status-bar__metric" title="Live entities (excludes merged)">
              {status.totalEntities !== undefined ? (
                status.totalEntities.toLocaleString()
              ) : (
                <span style={{ color: 'var(--fg-4)' }}>—</span>
              )}
              <span className="status-bar__metric-unit">entities</span>
            </span>
          </>
        )}
        {leftSlot.map((entry) => (
          <span key={entry.key}>{entry.element}</span>
        ))}
      </div>

      <div className="status-bar__slot status-bar__slot--right">
        {/* Service-health row — flatten the dependencies map into mono
            "svc ok / svc down" indicators. The v3 spec includes a retrieval
            latency value (`retrieval 12ms`); v1 only knows reachability. */}
        {status !== undefined &&
          Object.entries(status.dependencies).map(([dep, ok]) => (
            <span
              key={dep}
              className={`status-bar__dep status-bar__dep--${ok ? 'ok' : 'bad'}`}
              title={`${dep}: ${ok ? 'reachable' : 'unreachable'}`}
            >
              {dep} {ok ? 'ok' : 'down'}
            </span>
          ))}
        {/* Session cost telemetry removed 2026-05-21. */}
        {/* Build version pill — 2026-05-18. FE sha is from `vite define`; BE sha
            arrives via /system/status. Both also on window.__ALETHEIA_VERSION__ +
            status.backendVersion. Logged once on boot (`installVersionGlobals`). */}
        <span className="status-bar__sep" aria-hidden="true">·</span>
        <span
          className="status-bar__version"
          title={
            `Frontend build ${APP_VERSION} · built ${formatBuildTime()} (raw: ${BUILD_TIME})` +
            (status?.backendVersion
              ? `\nBackend build ${status.backendVersion}`
              : '')
          }
        >
          <span className="status-bar__version-label">fe</span>
          <span className="status-bar__version-sha">{APP_VERSION}</span>
          {status?.backendVersion !== undefined && (
            <>
              <span className="status-bar__version-sep" aria-hidden="true">/</span>
              <span className="status-bar__version-label">be</span>
              <span className="status-bar__version-sha">{status.backendVersion}</span>
            </>
          )}
        </span>
        {/* Center + right shell-mount slots (chat token-cost indicator etc.)
            now follow the version pill so the version sits in a stable place
            regardless of which guest content registers a slot. */}
        {centerSlot.map((entry) => (
          <span key={entry.key}>{entry.element}</span>
        ))}
        {rightSlot.map((entry) => (
          <span key={entry.key}>{entry.element}</span>
        ))}
      </div>
    </footer>
  );
}
